import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Clock, CheckCircle, XCircle, AlertTriangle, PlayCircle, 
  RefreshCw, Loader2, ChevronDown, ChevronUp, Activity,
  Mail, Trash2, RotateCcw, Zap
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import AdminSidebar from '../../components/admin/AdminSidebar';

const API_URL = window.location.origin;

export default function AdminCronMonitorPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [selectedJob, setSelectedJob] = useState(null);
  const [triggering, setTriggering] = useState(null);
  const [expandedJob, setExpandedJob] = useState(null);
  const [schedulerRunning, setSchedulerRunning] = useState(false);

  const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token') || localStorage.getItem('adminToken');

  useEffect(() => {
    if (!token) {
      navigate('/login-goturkeytransfer');
    }
  }, [token, navigate]);

  const fetchJobs = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/cron/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.status === 401) {
        navigate('/login-goturkeytransfer');
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setJobs(data.jobs);
        setSchedulerRunning(data.scheduler_running || false);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  }, [token, navigate]);

  const fetchLogs = useCallback(async (jobName = null, page = 1) => {
    try {
      let url = `${API_URL}/api/admin/cron/logs?page=${page}&limit=20`;
      if (jobName) url += `&job_name=${jobName}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        setLogs(data.logs);
        setLogsTotalPages(data.totalPages);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchJobs(), fetchLogs()]);
      setLoading(false);
    };
    loadData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchJobs, 30000);
    return () => clearInterval(interval);
  }, [fetchJobs, fetchLogs]);

  const handleTriggerJob = async (jobName) => {
    setTriggering(jobName);
    try {
      const response = await fetch(`${API_URL}/api/admin/cron/trigger/${jobName}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Job "${jobName}" triggered successfully`);
        // Refresh after a short delay
        setTimeout(() => {
          fetchJobs();
          fetchLogs(selectedJob);
        }, 2000);
      } else {
        toast.error(data.message || 'Failed to trigger job');
      }
    } catch (error) {
      toast.error('Failed to trigger job');
    } finally {
      setTriggering(null);
    }
  };

  const handleJobFilter = (jobName) => {
    setSelectedJob(jobName);
    setLogsPage(1);
    fetchLogs(jobName, 1);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="h-3 w-3" /> Success
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-400">
            <XCircle className="h-3 w-3" /> Failed
          </span>
        );
      case 'never_run':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
            <Clock className="h-3 w-3" /> Never Run
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-400">
            <AlertTriangle className="h-3 w-3" /> {status}
          </span>
        );
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('tr-TR');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex admin-theme">
        <AdminSidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex admin-theme">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Cron Job Monitor</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Monitor and manage scheduled background tasks
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => { fetchJobs(); fetchLogs(selectedJob, logsPage); }}
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Info Banner - Scheduler Status */}
      {schedulerRunning ? (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Zap className="h-5 w-5 text-green-500 mt-0.5" />
            <div>
              <h3 className="font-medium text-green-400">Otomatik Zamanlayıcı Aktif</h3>
              <p className="text-sm text-green-400/80 mt-1">
                Background scheduler çalışıyor. Tüm job'lar planlandığı zamanda otomatik olarak çalışacak.
                Manuel tetikleme için "Run Now" butonunu kullanabilirsiniz.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-400">Background Tasks Notice</h3>
              <p className="text-sm text-amber-400/80 mt-1">
                Automatic cron jobs require Redis/Celery which is not enabled in the current deployment. 
                You can manually trigger jobs using the "Run Now" button below. 
                Jobs will execute directly without queueing.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Job Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {jobs.map((job) => (
          <Card 
            key={job.name} 
            className={`cursor-pointer transition-all hover:shadow-md ${
              expandedJob === job.name ? 'ring-2 ring-orange-500' : ''
            }`}
            onClick={() => setExpandedJob(expandedJob === job.name ? null : job.name)}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-sm font-medium text-foreground">
                    {job.name}
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">{job.schedule}</p>
                </div>
                {getStatusBadge(job.last_status)}
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-3">{job.description}</p>
              
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  Last run: {formatDate(job.last_run)}
                </span>
                <span className="text-muted-foreground">
                  {job.run_count} runs
                </span>
              </div>
              
              {/* Next Run Info */}
              {job.next_run && (
                <div className="mt-2 text-xs text-green-400 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Next: {formatDate(job.next_run)}
                </div>
              )}

              {expandedJob === job.name && (
                <div className="mt-4 pt-4 border-t space-y-3">
                  {job.last_error && (
                    <div className="p-2 bg-red-500/10 rounded text-xs text-red-400">
                      <strong>Last Error:</strong> {job.last_error}
                    </div>
                  )}
                  
                  {job.last_details && (
                    <div className="p-2 bg-muted/30 rounded text-xs">
                      <strong className="text-foreground">Last Details:</strong>
                      <pre className="mt-1 text-muted-foreground overflow-auto">
                        {JSON.stringify(job.last_details, null, 2)}
                      </pre>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTriggerJob(job.name);
                      }}
                      disabled={triggering === job.name}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid={`trigger-${job.name}`}
                    >
                      {triggering === job.name ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <PlayCircle className="h-4 w-4 mr-1" />
                          Run Now
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleJobFilter(job.name);
                      }}
                      className="text-foreground"
                    >
                      View Logs
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Logs Section */}
      <Card className="bg-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-foreground">Execution Logs</CardTitle>
            {selectedJob && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleJobFilter(null)}
                className="text-sm text-foreground hover:text-foreground"
              >
                Clear Filter ({selectedJob})
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-border bg-zinc-800">
                  <th className="text-left p-3 font-semibold text-zinc-100">Job</th>
                  <th className="text-left p-3 font-semibold text-zinc-100">Status</th>
                  <th className="text-left p-3 font-semibold text-zinc-100">Executed At</th>
                  <th className="text-left p-3 font-semibold text-zinc-100">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center p-8 text-zinc-400">
                      No logs found
                    </td>
                  </tr>
                ) : (
                  logs.map((log, idx) => (
                    <tr key={idx} className="border-b border-zinc-700 hover:bg-zinc-800/50">
                      <td className="p-3 font-medium text-zinc-200">{log.job_name}</td>
                      <td className="p-3">{getStatusBadge(log.status)}</td>
                      <td className="p-3 text-zinc-400">{formatDate(log.executed_at)}</td>
                      <td className="p-3">
                        {log.error ? (
                          <span className="text-red-400 text-xs">{log.error}</span>
                        ) : log.details ? (
                          <span className="text-zinc-400 text-xs">
                            {JSON.stringify(log.details).substring(0, 50)}...
                          </span>
                        ) : (
                          <span className="text-zinc-500">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {logsTotalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button
                variant="secondary"
                size="sm"
                disabled={logsPage === 1}
                onClick={() => {
                  setLogsPage(logsPage - 1);
                  fetchLogs(selectedJob, logsPage - 1);
                }}
                className="text-zinc-200"
              >
                Previous
              </Button>
              <span className="text-sm text-zinc-400">
                Page {logsPage} of {logsTotalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={logsPage === logsTotalPages}
                onClick={() => {
                  setLogsPage(logsPage + 1);
                  fetchLogs(selectedJob, logsPage + 1);
                }}
                className="text-zinc-200"
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
        </div>
      </main>
    </div>
  );
}
